/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { SearchActions, toTitleCase } from 'sarsaparilla';

import type {
    ContentSuggestion,
    InventorySuggestion,
    LocationSuggestion,
} from 'shared-ui/api/search/search.types';
import { getTargetUri } from 'shared-ui/maps/helpers/mapHelper';
import { containsEmail } from 'shared-ui/search-autosuggest/src/SearchAutosuggest.utils';

import type { SearchSuggestionItem, SuggestionSource } from './QuickSearch.types';

/** Persisted search state that `/search` and facility pages hydrate from on load. */
const SEARCH_SESSION_KEY = 'r1s_search_session';

/** Entity types that link straight to their own page even when they aren't reservable. */
const DIRECT_LINK_ENTITY_TYPES = new Set(['recarea', 'kb', 'cms', 'page', 'pass']);

/** Radius around a location suggestion — matches `SearchActions.submitSimpleSearch`. */
const LOCATION_SEARCH_RADIUS = '200';

const EVENT_CATEGORY = 'Global Navigation';

export type SuggestionNavigation = {
    url: string;
    event: { category: string; action: string; label: string };
    /** What to record in search history, or null to record nothing */
    history: { rawSearchText: string; suggestion: SuggestionSource | null } | null;
};

/** Any item that goes somewhere when selected — everything but the clear-history row. */
export type NavigableSuggestionItem = Exclude<
    SearchSuggestionItem,
    { type: 'clear-history' }
>;

/** Every field a suggestion source might carry, so helpers can read them without narrowing. */
type SuggestionFields = Partial<
    InventorySuggestion & LocationSuggestion & ContentSuggestion
>;

type SearchParams = Record<string, string | number | null | undefined>;

/**
 * Decides where selecting a quick-search item goes and what it records along the way.
 *
 * Reservable inventory and a handful of entity types link straight to their own page.
 * Everything else — free text, locations, non-reservable inventory, near me — becomes a
 * fresh `/search`, built from the URL alone, with nothing carried over from the previous
 * search.
 */
export function planSuggestionNavigation(
    item: NavigableSuggestionItem
): SuggestionNavigation {
    if (item.type === 'near-me') {
        return {
            url: buildSearchUrl({}),
            event: {
                category: 'Search',
                action: 'Select Search Suggestion Near Me',
                label: 'Near Me',
            },
            history: null,
        };
    }

    if (item.type === 'query') {
        return {
            url: buildSearchUrl({ q: item.text }),
            event: createEvent('Submit Search', item.text),
            history: { rawSearchText: item.text, suggestion: null },
        };
    }

    if (item.type === 'location') {
        const place = toPlace(item.source);

        return {
            url: buildSearchUrl(getSuggestionSearchParams(place, place.text)),
            event: createEvent('Select Search Suggestion', place.name || place.text),
            history: { rawSearchText: place.text, suggestion: place },
        };
    }

    const { source } = item;
    const searchText = getSearchText(source);
    const event = createEvent(
        item.type === 'recent'
            ? 'Select Search History Suggestion'
            : 'Select Search Suggestion',
        source.name || source.text
    );
    const history = { rawSearchText: searchText, suggestion: source };

    if (linksDirectly(source)) {
        return {
            url: getTargetUri(source),
            event,
            history,
        };
    }

    return {
        url: buildSearchUrl(getSuggestionSearchParams(source, searchText)),
        event,
        history,
    };
}

/** Whether selecting the item starts a new `/search` rather than opening its own page. */
export function startsNewSearch(item: NavigableSuggestionItem) {
    return (
        item.type === 'query' ||
        item.type === 'near-me' ||
        item.type === 'location' ||
        !linksDirectly(item.source)
    );
}

/**
 * Discards the persisted search state, records the selection in search history, reports
 * it to analytics, then leaves the page.
 */
export async function navigateToSuggestion(item: NavigableSuggestionItem) {
    const navigation = planSuggestionNavigation(item);

    // Every destination hydrates its search state from this, so the previous search's
    // filters would carry over — onto facility pages too — if it were left behind
    try {
        window.localStorage.removeItem(SEARCH_SESSION_KEY);
    } catch {
        // storage unavailable — nothing to clear
    }

    if (navigation.history) {
        await saveSearchHistory(navigation.history);
    }

    SearchActions.trackRunSearch(navigation.event, () => {
        window.location.assign(navigation.url);
    });
}

async function saveSearchHistory(history: NonNullable<SuggestionNavigation['history']>) {
    try {
        // addSearchHistoryItem writes sarsaparilla's in-memory history cache back to
        // localStorage. The palette's history query loads that cache whenever it opens,
        // so it's already filled here — reloading would only delay navigation.
        await SearchActions.addSearchHistoryItem({
            searchHistory: undefined,
            ...history,
        });
    } catch (e) {
        // A failed history save shouldn't block navigation
        // eslint-disable-next-line no-console
        console.log('error saving history', e);
    }
}

function createEvent(action: string, label: string) {
    return {
        category: EVENT_CATEGORY,
        action,
        label: containsEmail(label) ? '[Redacted Email]' : label,
    };
}

/**
 * A location suggestion without the inventory fields some places (rec areas, say) come
 * with, so it's searched around like any other place — and replays that way from history.
 */
function toPlace(source: LocationSuggestion): LocationSuggestion {
    const { city, country_code, lat, lng, lat_ne, lng_ne, lat_sw, lng_sw } = source;
    const { name, state_code, text } = source;

    return {
        city,
        country_code,
        lat,
        lng,
        lat_ne,
        lng_ne,
        lat_sw,
        lng_sw,
        name,
        state_code,
        text,
    };
}

function getSearchText({ text = '', entity_id, entity_type }: SuggestionFields) {
    return entity_id && entity_type ? toTitleCase(text) : text;
}

function linksDirectly({ reservable, entity_type }: SuggestionFields) {
    return !!reservable || DIRECT_LINK_ENTITY_TYPES.has(entity_type ?? '');
}

function getSuggestionSearchParams(
    source: SuggestionFields,
    searchText: string
): SearchParams {
    const { entity_id, entity_type, lat, lng, lat_sw, lng_sw, lat_ne, lng_ne } = source;
    const params: SearchParams = { q: searchText, entity_id, entity_type };

    // Inventory carries coordinates too, but only a place is searched around
    if (entity_type || !lat || !lng) {
        return params;
    }

    // Large areas like states come with a bounding box, which wins over the center point
    const hasBounds = lat_sw && lng_sw && lat_ne && lng_ne;

    return {
        ...params,
        ...(hasBounds ? { lat_sw, lng_sw, lat_ne, lng_ne } : { lat, lng }),
        radius: LOCATION_SEARCH_RADIUS,
    };
}

function buildSearchUrl(params: SearchParams) {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            searchParams.append(key, String(value));
        }
    });

    const query = searchParams.toString();

    return query ? `/search?${query}` : '/search';
}

// cSpell:ignore recarea
